import { useState, useEffect } from 'react';
import { FiCheckCircle } from 'react-icons/fi';

const WAITING_MESSAGES = [
  'Connecting to your store...',
  'Fetching your order data...',
  'Analyzing customer behavior...',
  'Identifying revenue opportunities...',
  'Generating personalized insights...',
];

type WaitingStateCardProps = {
  isGenerating: boolean;
  onOpenChat?: () => void;
};

type StepStatus = 'pending' | 'active' | 'completed';

function ProgressStep({
  number,
  label,
  status,
}: {
  number: number;
  label: string;
  status: StepStatus;
}) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={`
          w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
          transition-all duration-500
          ${status === 'completed'
            ? 'bg-success text-success-content shadow-[0_0_12px_rgba(34,197,94,0.5)]'
            : status === 'active'
              ? 'bg-primary text-primary-content shadow-[0_0_12px_rgba(250,204,21,0.5)] animate-pulse'
              : 'bg-base-300 text-base-content/50'
          }
        `}
      >
        {status === 'completed' ? <FiCheckCircle className="text-lg" /> : number}
      </div>
      <span
        className={`text-sm transition-colors duration-300 ${
          status === 'pending' ? 'text-base-content/50' : 'text-base-content'
        }`}
      >
        {label}
      </span>
    </div>
  );
}

export default function WaitingStateCard({
  isGenerating,
}: WaitingStateCardProps) {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % WAITING_MESSAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Determine step statuses based on current state
  const getStepStatus = (step: number): StepStatus => {
    if (isGenerating) {
      // Report is generating - steps 1 & 2 complete, step 3 active
      if (step <= 2) return 'completed';
      return 'active';
    } else {
      // Still syncing - step 1 complete, step 2 active, step 3 pending
      if (step === 1) return 'completed';
      if (step === 2) return 'active';
      return 'pending';
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-[calc(100vh-10rem)]">
      <div className="card bg-base-200 shadow-xl max-w-md w-full mx-4">
        <div className="card-body items-center text-center py-6 px-6 gap-3">
          {/* Spinner */}
          <span className="loading loading-spinner loading-lg text-primary" />

          {/* Title */}
          <h2 className="text-xl font-semibold">
            {isGenerating ? 'Generating Your Report' : 'Syncing Your Data'}
          </h2>

          {/* Cycling message */}
          <p className="text-base-content/60 text-sm h-5">
            {WAITING_MESSAGES[messageIndex]}
          </p>

          {/* Progress steps with glow effect */}
          <div className="flex flex-col gap-2 w-full mt-2 mb-1">
            <ProgressStep number={1} label="Connect store" status={getStepStatus(1)} />
            <div className="w-0.5 h-3 bg-base-300 ml-4" />
            <ProgressStep number={2} label="Sync data" status={getStepStatus(2)} />
            <div className="w-0.5 h-3 bg-base-300 ml-4" />
            <ProgressStep number={3} label="Generate insights" status={getStepStatus(3)} />
          </div>

          {/* Close tab notice */}
          <div className="bg-base-300 rounded-lg py-2.5 px-4 w-full mt-1">
            <p className="text-sm font-medium">You can close this tab</p>
            <p className="text-xs text-base-content/60">
              We'll email you when ready
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
